import React from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  PortalTableCell,
  PortalTableHead,
  PortalTableRow,
} from '../../../../styles/portalListTable';
import { getPortalCardSx } from '../../../../styles/portalSurfaces';
import { usePortalTokens } from '../../../../styles/usePortalTokens';

const ActionIconButton = styled(IconButton)(({ theme, bordercolor, iconcolor }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  border: `1px solid ${bordercolor ?? theme.palette.divider}`,
  color: iconcolor ?? theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CompatibilityBadge = ({ value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: 'success.main',
        flexShrink: 0,
      }}
    />
    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {value}% Match
    </Typography>
  </Box>
);

const CommunityJoinRequestsPanel = ({
  requests = [],
  loading = false,
  onAccept,
  onDecline,
  onViewProfile,
}) => {
  const portal = usePortalTokens();

  return (
    <Box
      sx={{
        ...getPortalCardSx(portal, { borderRadius: '12px' }),
        p: { xs: 2, md: 2.5 },
        height: '100%',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', mb: 2 }}>
        Join Requests
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} color="primary" />
        </Box>
      ) : requests.length ? (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Table sx={{ minWidth: 720 }}>
            <PortalTableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Compatibility</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </PortalTableHead>
            <TableBody>
              {requests.map((request) => (
                <PortalTableRow key={request.id}>
                  <PortalTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={request.avatarUrl}
                        alt={request.name}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                          {request.name}
                        </Typography>
                        {request.role ? (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', lineHeight: 1.3 }}
                          >
                            {request.role}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </PortalTableCell>
                  <PortalTableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {request.gender || '—'}
                    </Typography>
                  </PortalTableCell>
                  <PortalTableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                      {request.email || '—'}
                    </Typography>
                    {request.phone ? (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                        {request.phone}
                      </Typography>
                    ) : null}
                  </PortalTableCell>
                  <PortalTableCell>
                    {request.compatibility != null ? (
                      <CompatibilityBadge value={request.compatibility} />
                    ) : (
                      '—'
                    )}
                  </PortalTableCell>
                  <PortalTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ActionIconButton
                        bordercolor="#86efac"
                        iconcolor="#16a34a"
                        aria-label={`Accept ${request.name}`}
                        onClick={() => onAccept?.(request)}
                      >
                        <CheckOutlinedIcon sx={{ fontSize: 18 }} />
                      </ActionIconButton>
                      <ActionIconButton
                        bordercolor="#fca5a5"
                        iconcolor="#dc2626"
                        aria-label={`Decline ${request.name}`}
                        onClick={() => onDecline?.(request)}
                      >
                        <CloseOutlinedIcon sx={{ fontSize: 18 }} />
                      </ActionIconButton>
                      <ActionIconButton
                        bordercolor="#bdbdbd"
                        iconcolor="#757575"
                        aria-label={`View ${request.name}`}
                        onClick={() => onViewProfile?.(request)}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                      </ActionIconButton>
                    </Box>
                  </PortalTableCell>
                </PortalTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No pending join requests.
        </Typography>
      )}
    </Box>
  );
};

export default CommunityJoinRequestsPanel;
